/*
 * <delivery-date-picker> — the "Choose Your Delivery Date" dialog (TSK-10772).
 *
 * A vanilla month grid, no library: the design (lime tiles with a dot for
 * every deliverable day, outside-month days still selectable, a confirm CTA
 * that names the pending date) is specific enough that a plugin would only
 * be fought with CSS.
 *
 * The element owns presentation only. WHICH dates are selectable is decided
 * by the caller, which passes them in — the plan builder derives them from
 * window.DeliverySchedule.firstDeliveryOptions(). Every Date is "store-local"
 * (UTC fields hold the store's wall clock), so all reads are getUTC*() and
 * all formatting goes through DeliverySchedule — see assets/delivery-schedule.js.
 *
 *   picker.open({
 *     options: [Date, ...],      // selectable days, earliest first
 *     selected: "2026-09-13",    // ISO of the current choice (optional)
 *     onConfirm: (iso) => {},    // fired once the customer presses "Use ..."
 *   });
 *
 * Dependencies (deferred, in this order): delivery-schedule.js, focus-trap.js.
 */
if (!customElements.get("delivery-date-picker")) {
  const CELLS = 42; // 6 rows × 7 — fixed so the panel never changes height month to month

  customElements.define(
    "delivery-date-picker",
    class DeliveryDatePicker extends HTMLElement {
      constructor() {
        super();
        this.options = [];
        this.optionKeys = new Set();
        // The tile the customer has tapped but not yet confirmed.
        this.pending = null;
        this.viewYear = 0;
        this.viewMonth = 0;
        this.onConfirm = null;
        this.opener = null;
        this._releaseTrap = null;
        this._onClick = this._onClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
      }

      connectedCallback() {
        this.panel = this.querySelector("[data-date-picker-panel]");
        this.grid = this.querySelector("[data-date-picker-grid]");
        this.monthEl = this.querySelector("[data-date-picker-month]");
        this.confirmBtn = this.querySelector("[data-date-picker-confirm]");
        this.prevBtn = this.querySelector("[data-date-picker-prev]");
        this.nextBtn = this.querySelector("[data-date-picker-next]");
        this.confirmTemplate = this.dataset.confirmLabel || "Use __DATE__";
        this.addEventListener("click", this._onClick);
        this.renderWeekdays();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this._onClick);
        this.close({ restoreFocus: false });
      }

      get schedule() {
        return window.DeliverySchedule;
      }

      get isOpen() {
        return !this.hidden;
      }

      /* ---- Public API ------------------------------------------------ */

      open({ options, selected, onConfirm } = {}) {
        const DS = this.schedule;
        if (!DS || !Array.isArray(options) || options.length === 0 || !this.grid) return;

        this.options = options;
        this.optionKeys = new Set(options.map((d) => DS.toISODate(d)));
        this.onConfirm = typeof onConfirm === "function" ? onConfirm : null;
        // Never open on a date the customer can't have: fall back to the earliest.
        this.pending = this.optionKeys.has(selected) ? selected : DS.toISODate(options[0]);

        const anchor = DS.fromISODate(this.pending);
        this.viewYear = anchor.getUTCFullYear();
        this.viewMonth = anchor.getUTCMonth();

        this.opener = document.activeElement;
        this.hidden = false;
        this.render();

        document.addEventListener("keydown", this._onKeydown);
        this._releaseTrap = window.FocusTrap ? window.FocusTrap.trap(this.panel) : null;
        // Same scroll lock the meal-info modal uses (section-plan-builder.js).
        document.body.style.overflow = "hidden";

        const initial = this.grid.querySelector(".is-selected") || this.grid.querySelector("[data-date-picker-day]");
        (initial || this.confirmBtn).focus();
        this.dispatchEvent(new CustomEvent("date-picker:open", { bubbles: true }));
      }

      close({ restoreFocus = true } = {}) {
        if (this.hidden) return;
        this.hidden = true;
        document.removeEventListener("keydown", this._onKeydown);
        if (this._releaseTrap) {
          this._releaseTrap();
          this._releaseTrap = null;
        }
        document.body.style.overflow = "";
        if (restoreFocus && this.opener && typeof this.opener.focus === "function") {
          this.opener.focus();
        }
        this.opener = null;
        this.dispatchEvent(new CustomEvent("date-picker:close", { bubbles: true }));
      }

      /* ---- Events ---------------------------------------------------- */

      _onClick(event) {
        if (event.target.closest("[data-date-picker-close]")) {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.target.closest("[data-date-picker-prev]")) {
          event.preventDefault();
          this.shiftMonth(-1);
          return;
        }
        if (event.target.closest("[data-date-picker-next]")) {
          event.preventDefault();
          this.shiftMonth(1);
          return;
        }
        const day = event.target.closest("[data-date-picker-day]");
        if (day) {
          event.preventDefault();
          this.select(day.dataset.iso);
          return;
        }
        if (event.target.closest("[data-date-picker-confirm]")) {
          event.preventDefault();
          this.confirm();
        }
      }

      _onKeydown(event) {
        if (event.key === "Escape") {
          event.preventDefault();
          this.close();
          return;
        }
        // Arrow keys walk the selectable days only — unavailable tiles are
        // inert <span>s, so stepping by calendar offsets would strand focus.
        const day = event.target && event.target.closest && event.target.closest("[data-date-picker-day]");
        if (!day) return;
        const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[event.key];
        if (!step) return;
        event.preventDefault();
        this.focusOption(day.dataset.iso, step);
      }

      /* ---- State ----------------------------------------------------- */

      select(iso) {
        if (!this.optionKeys.has(iso)) return;
        this.pending = iso;
        this.render();
        const btn = this.grid.querySelector(`[data-iso="${iso}"]`);
        if (btn) btn.focus();
      }

      confirm() {
        const iso = this.pending;
        const handler = this.onConfirm;
        this.close();
        if (iso && handler) handler(iso);
      }

      // Move focus to the option `step` places away in the options list,
      // paging the calendar to its month when needed.
      focusOption(iso, step) {
        const DS = this.schedule;
        const keys = this.options.map((d) => DS.toISODate(d));
        const idx = keys.indexOf(iso);
        const nextIso = keys[idx + step];
        if (idx === -1 || !nextIso) return;
        const next = DS.fromISODate(nextIso);
        if (!this.grid.querySelector(`[data-iso="${nextIso}"]`)) {
          this.viewYear = next.getUTCFullYear();
          this.viewMonth = next.getUTCMonth();
          this.render();
        }
        const btn = this.grid.querySelector(`[data-iso="${nextIso}"]`);
        if (btn) btn.focus();
      }

      // Months are only navigable between the first and last option.
      monthIndex(year, month) {
        return year * 12 + month;
      }

      canShow(year, month) {
        const first = this.options[0];
        const last = this.options[this.options.length - 1];
        if (!first || !last) return false;
        const idx = this.monthIndex(year, month);
        return idx >= this.monthIndex(first.getUTCFullYear(), first.getUTCMonth())
          && idx <= this.monthIndex(last.getUTCFullYear(), last.getUTCMonth());
      }

      shiftMonth(delta) {
        let month = this.viewMonth + delta;
        let year = this.viewYear;
        if (month < 0) { month = 11; year -= 1; }
        if (month > 11) { month = 0; year += 1; }
        if (!this.canShow(year, month)) return;
        this.viewYear = year;
        this.viewMonth = month;
        this.render();
        // The arrow that just became disabled would drop focus on the body;
        // keep it on whichever nav control is still live.
        if (document.activeElement && document.activeElement.disabled) {
          (delta < 0 ? this.nextBtn : this.prevBtn).focus();
        }
      }

      /* ---- Rendering ------------------------------------------------- */

      // Monday-first, from the same locale the dates use, so the abbreviations
      // can't drift from the rest of the copy. CSS upper-cases them.
      renderWeekdays() {
        const el = this.querySelector("[data-date-picker-weekdays]");
        if (!el) return;
        const monday = new Date(Date.UTC(2024, 0, 1)); // 1 Jan 2024 was a Monday
        const frag = document.createDocumentFragment();
        for (let i = 0; i < 7; i++) {
          const d = new Date(monday);
          d.setUTCDate(1 + i);
          const span = document.createElement("span");
          span.className = "date-picker__weekday";
          span.textContent = d.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
          frag.appendChild(span);
        }
        el.replaceChildren(frag);
      }

      render() {
        const DS = this.schedule;
        const first = new Date(Date.UTC(this.viewYear, this.viewMonth, 1));
        this.monthEl.textContent = DS.formatMonthYear(first);

        // Lead-in from the Monday on or before the 1st.
        const lead = (first.getUTCDay() + 6) % 7;
        const start = DS.addDays(first, -lead);
        const frag = document.createDocumentFragment();

        for (let i = 0; i < CELLS; i++) {
          const d = DS.addDays(start, i);
          const iso = DS.toISODate(d);
          const available = this.optionKeys.has(iso);
          const outside = d.getUTCMonth() !== this.viewMonth;

          // Only deliverable days are controls; the rest are decoration.
          const el = document.createElement(available ? "button" : "span");
          el.className = "date-picker__day";
          if (outside) el.classList.add("date-picker__day--outside");
          el.textContent = String(d.getUTCDate());

          if (available) {
            el.type = "button";
            el.classList.add("date-picker__day--available");
            el.dataset.datePickerDay = "";
            el.dataset.iso = iso;
            el.setAttribute("aria-label", `${DS.formatLongComma(d)} ${d.getUTCFullYear()}`);
            const selected = iso === this.pending;
            el.setAttribute("aria-pressed", selected ? "true" : "false");
            if (selected) el.classList.add("is-selected");
          } else {
            el.setAttribute("aria-hidden", "true");
          }
          frag.appendChild(el);
        }
        this.grid.replaceChildren(frag);

        const prev = this.viewMonth === 0 ? [this.viewYear - 1, 11] : [this.viewYear, this.viewMonth - 1];
        const next = this.viewMonth === 11 ? [this.viewYear + 1, 0] : [this.viewYear, this.viewMonth + 1];
        this.prevBtn.disabled = !this.canShow(prev[0], prev[1]);
        this.nextBtn.disabled = !this.canShow(next[0], next[1]);

        const pendingDate = DS.fromISODate(this.pending);
        this.confirmBtn.textContent = this.confirmTemplate.replace(
          "__DATE__",
          pendingDate ? DS.formatLongComma(pendingDate) : ""
        );
        this.confirmBtn.disabled = !pendingDate;
      }
    }
  );
}
